import React, { Component } from "react";
import ReactDOM from "react-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import HelperClass from "../../../config/helperClass";
import { listStages, saveStages, addStage, removeStage, listLeadsKanban, getScoreWeights, saveScoreWeights } from "./api/leads_api";
import { THEME_COLOR, THEME_TEXT_COLOR } from "../../../config/config";
import NotificationToast from "../../../components/Reseller/NotificationToast";

const auth = new HelperClass();

const WEIGHT_DEFAULTS = { engagement: 30, deal_value: 25, stage_progression: 20, activity_recency: 15, priority: 10 };

const WEIGHT_FACTORS = [
  { key: "engagement",        label: "Engagement",         desc: "Email, call & note count — 10 interactions = 100" },
  { key: "deal_value",        label: "Deal Value",         desc: "Expected deal value — up to $100k = 100" },
  { key: "stage_progression", label: "Stage Progression",  desc: "How far along the pipeline (by stage order)" },
  { key: "activity_recency",  label: "Activity Recency",   desc: "Days since last touchpoint — ≤1 day = 100, 30+ days = 0" },
  { key: "priority",          label: "Priority",           desc: "Lead priority: High=100, Med=60, Low=30" },
];

/* ──────────────────────────────────────────────────────────────────
 * StageRow — memoized so editing one stage / dragging doesn't
 * re-render every other row's native <input type="color"> widgets
 * (which is what made the table feel sluggish).
 * ────────────────────────────────────────────────────────────────── */
const StageRow = React.memo(function StageRow(props) {
  const {
    stage: s,
    index: i,
    provided,
    snapshot,
    isLocked,
    isHighlight,
    leadsCount,
    isRemoving,
    onPatch,
    onRemove,
  } = props;
  return (
    <tr
      ref={provided.innerRef}
      {...provided.draggableProps}
      data-stage-key={s.stage_key}
      className="tb-tnx-item"
      style={{
        ...provided.draggableProps.style,
        background: snapshot.isDragging ? "#ffffff" : (isHighlight ? "#fef9c3" : "transparent"),
        boxShadow: snapshot.isDragging ? "0 5px 15px rgba(0,0,0,0.15)" : "none",
        opacity: isLocked ? 0.7 : 1,
      }}
    >
      <td
        className="tb-tnx-info"
        style={{ textAlign: "center", color: isLocked ? "#ddd" : "#aaa", cursor: isLocked ? "not-allowed" : "grab" }}
        {...(isLocked ? {} : provided.dragHandleProps)}
      >
        {isLocked ? (
          <em className="icon ni ni-lock-alt" style={{ fontSize: 14 }}></em>
        ) : (
          <em className="icon ni ni-menu" style={{ fontSize: 16 }}></em>
        )}
      </td>
      <td className="tb-tnx-info">
        <input
          type="text"
          className="form-control form-control-sm"
          value={s.label || ""}
          onChange={(e) => onPatch(s.stage_key, "label", e.target.value)}
          disabled={s.is_system === 1}
          placeholder="Stage name"
          style={{ fontWeight: 500 }}
        />
        {s.is_system ? <small className="text-muted">System stage</small> : null}
        {s.is_won ? <small style={{ display: "block", fontWeight: 700, color: "#526484", fontSize: 11 }}>✓ Won</small> : null}
        {s.is_lost ? <small style={{ display: "block", fontWeight: 700, color: "#526484", fontSize: 11 }}>✗ Lost</small> : null}
      </td>
      <td className="tb-tnx-info">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            display: "inline-flex", alignItems: "center",
            border: "1px solid #e5e9f2", borderRadius: 8,
            background: "#f8fafe", overflow: "hidden",
            opacity: isLocked ? 0.5 : 1,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRight: "1px solid #e5e9f2" }}>
              <input
                type="color"
                value={s.color_hex || "#1C5BDE"}
                onChange={(e) => onPatch(s.stage_key, "color_hex", e.target.value)}
                disabled={isLocked}
                style={{ width: 20, height: 20, borderRadius: 4, border: "none", background: "none", cursor: isLocked ? "not-allowed" : "pointer", padding: 0, pointerEvents: isLocked ? "none" : "auto" }}
                title={isLocked ? "System stage color cannot be changed" : "Text color"}
              />
              <span style={{ fontSize: 11, color: "#6b7a99", fontWeight: 600, letterSpacing: "0.02em" }}>Text</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px" }}>
              <input
                type="color"
                value={s.bg_hex || "#E8EEFB"}
                onChange={(e) => onPatch(s.stage_key, "bg_hex", e.target.value)}
                disabled={isLocked}
                style={{ width: 20, height: 20, borderRadius: 4, border: "none", background: "none", cursor: isLocked ? "not-allowed" : "pointer", padding: 0, pointerEvents: isLocked ? "none" : "auto" }}
                title={isLocked ? "System stage color cannot be changed" : "Background color"}
              />
              <span style={{ fontSize: 11, color: "#6b7a99", fontWeight: 600, letterSpacing: "0.02em" }}>BG</span>
            </div>
          </div>
        </div>
      </td>
      <td className="tb-tnx-info" style={{ textAlign: "center" }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{leadsCount}</span>
      </td>
      <td className="tb-tnx-info" style={{ textAlign: "center" }}>
        <div
          className="custom-control custom-switch"
          style={{ display: "inline-block", cursor: leadsCount > 0 ? "not-allowed" : "auto" }}
          title={leadsCount > 0 ? "Cannot change — stage has active leads" : ""}
        >
          <input
            type="checkbox"
            className="custom-control-input"
            id={"active_" + s.stage_key}
            checked={!!s.active}
            onChange={(e) => onPatch(s.stage_key, "active", e.target.checked ? 1 : 0)}
            disabled={s.is_system === 1 || leadsCount > 0}
          />
          <label className="custom-control-label" htmlFor={"active_" + s.stage_key}></label>
        </div>
      </td>
      <td className="tb-tnx-info" style={{ textAlign: "center" }}>
        {s.is_system ? (
          <span style={{ color: "#aaa", fontSize: 11 }}>—</span>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-icon btn-trigger"
            disabled={isRemoving || leadsCount > 0}
            style={{ cursor: isRemoving || leadsCount > 0 ? "not-allowed" : "pointer" }}
            onClick={() => onRemove(s.stage_key, i)}
            title={leadsCount > 0 ? "Cannot remove — stage has active leads" : "Remove stage"}
          >
            <em className="icon ni ni-cross" style={{ color: "#dc3545" }}></em>
          </button>
        )}
      </td>
    </tr>
  );
}, function areEqual(prev, next) {
  // Stage object refs change only for the patched row (immutable updates in parent),
  // so default shallow + this guard means typing/colour-picking in one row
  // does not re-render the other ~N rows or their native color widgets.
  return (
    prev.stage === next.stage &&
    prev.index === next.index &&
    prev.isLocked === next.isLocked &&
    prev.isHighlight === next.isHighlight &&
    prev.leadsCount === next.leadsCount &&
    prev.isRemoving === next.isRemoving &&
    prev.onPatch === next.onPatch &&
    prev.onRemove === next.onRemove &&
    prev.snapshot.isDragging === next.snapshot.isDragging &&
    prev.provided.draggableProps.style === next.provided.draggableProps.style
  );
});

export default class LeadsSettings extends Component {
  _stagesAutoSaveTimer = null;
  _lastSavedStagesSig = "";
  _isSaving = false;        // serialize silent saves without blocking edits
  _pendingSave = false;     // queue one more save if state changed mid-flight
  _stageCountMap = {};      // O(1) lookup for row leads count (replaces per-row .find)

  state = {
    stages: [],
    loading: true,
    saving: false,
    removing: null,
    pipelineData: [],
    loadingPipeline: false,
    newlyAddedKey: null,
    newStage: { label: "", color_hex: "#1C5BDE", bg_hex: "#E8EEFB" },
    // Score weights
    weights: Object.assign({}, WEIGHT_DEFAULTS),
    weightsLoading: true,
    weightsSaving: false,
    removeConfirm: null,
    toast: null,
  };

  showToast = (msg, type) => {
    clearTimeout(this._toastTimer);
    this.setState({ toast: { msg, type } });
    this._toastTimer = setTimeout(() => this.setState({ toast: null }), 2500);
  };

  async componentDidMount() {
    await this.reload();
    await this.loadPipelinePreview();
    await this.loadWeights();
  }

  componentDidUpdate(prevProps, prevState) {
    // Seed baseline after first successful load (or any reload), so initial data doesn't trigger save.
    if (prevState.loading && !this.state.loading) {
      this._lastSavedStagesSig = this.getStagesSignature(this.state.stages);
      this._recomputeStageCountMap(this.state.pipelineData);
      return;
    }

    if (prevState.pipelineData !== this.state.pipelineData) {
      this._recomputeStageCountMap(this.state.pipelineData);
    }

    if (prevState.stages !== this.state.stages) {
      this.scheduleStagesAutoSave();
    }
  }

  _recomputeStageCountMap = (pipelineData) => {
    const map = {};
    (pipelineData || []).forEach((p) => {
      if (p && p.stage && p.stage.stage_key) map[p.stage.stage_key] = p.count || 0;
    });
    this._stageCountMap = map;
  };

  componentWillUnmount() {
    if (this._stagesAutoSaveTimer) {
      clearTimeout(this._stagesAutoSaveTimer);
      this._stagesAutoSaveTimer = null;
    }
    clearTimeout(this._toastTimer);
  }

  getStagesForSave = (stages) => {
    return (stages || []).map((s, idx) => ({ ...s, sort_order: idx + 1 }));
  };

  getStagesSignature = (stages) => {
    const normalized = this.getStagesForSave(stages).map((s) => ({
      stage_key: s.stage_key,
      label: s.label || "",
      color_hex: s.color_hex || "",
      bg_hex: s.bg_hex || "",
      active: parseInt(s.active, 10) ? 1 : 0,
      is_system: parseInt(s.is_system, 10) ? 1 : 0,
      is_won: parseInt(s.is_won, 10) ? 1 : 0,
      is_lost: parseInt(s.is_lost, 10) ? 1 : 0,
      sort_order: s.sort_order,
    }));
    return JSON.stringify(normalized);
  };

  scheduleStagesAutoSave = () => {
    if (this.state.loading) return;
    const currentSig = this.getStagesSignature(this.state.stages);
    if (currentSig === this._lastSavedStagesSig) return;

    if (this._stagesAutoSaveTimer) {
      clearTimeout(this._stagesAutoSaveTimer);
    }

    // Debounce rapid typing/picker changes to avoid request spam.
    // 500 ms feels responsive for label edits; reorders bypass this entirely
    // (see onDragEnd — they call save() immediately on drop).
    this._stagesAutoSaveTimer = setTimeout(() => {
      this._stagesAutoSaveTimer = null;
      this.save({ silent: true, source: "autosave", refreshPipeline: true });
    }, 500);
  };

  reload = async () => {
    this.setState({ loading: true });
    const r = await listStages(auth.getAccount(), auth.getToken());
    if (r && r.data && r.data.status === 200) {
      const stages = (r.data.data || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      this.setState({ stages, loading: false });
    } else {
      this.setState({ loading: false });
      this.showToast("Failed to load stages.", "error");
    }
  };

  loadPipelinePreview = async () => {
    this.setState({ loadingPipeline: true });
    try {
      const r = await listLeadsKanban(auth.getAccount(), auth.getToken(), { per_column: 1 });
      if (r && r.data && r.data.status === 200 && r.data.data && r.data.data.columns) {
        const pipelineData = r.data.data.columns.map((col) => ({
          stage: col.stage,
          count: col.count || 0,
        }));
        this.setState({ pipelineData, loadingPipeline: false });
      } else {
        this.setState({ loadingPipeline: false });
      }
    } catch (_) {
      this.setState({ loadingPipeline: false });
    }
  };

  loadWeights = async () => {
    this.setState({ weightsLoading: true });
    const r = await getScoreWeights(auth.getAccount(), auth.getToken());
    if (r && r.data && r.data.status === 200 && r.data.data) {
      this.setState({ weights: r.data.data, weightsLoading: false });
    } else {
      this.setState({ weightsLoading: false });
    }
  };

  patchWeight = (key, value) => {
    const n = Math.min(100, Math.max(0, parseInt(value, 10) || 0));
    this.setState(function(s) { return { weights: Object.assign({}, s.weights, { [key]: n }) }; });
  };

  normalizeWeights = () => {
    const { weights } = this.state;
    const keys = WEIGHT_FACTORS.map(function(f) { return f.key; });
    const total = keys.reduce(function(a, k) { return a + (parseInt(weights[k]) || 0); }, 0);
    if (total === 0) {
      // Distribute evenly
      var each = 20;
      var next = Object.assign({}, weights);
      keys.forEach(function(k) { next[k] = each; });
      this.setState({ weights: next });
      return;
    }
    // Scale each weight proportionally so they sum to 100.
    var scaled = {};
    var runningTotal = 0;
    keys.forEach(function(k, i) {
      if (i === keys.length - 1) {
        scaled[k] = 100 - runningTotal;
      } else {
        var v = Math.round((parseInt(weights[k]) || 0) / total * 100);
        scaled[k] = v;
        runningTotal += v;
      }
    });
    this.setState({ weights: Object.assign({}, weights, scaled) });
  };

  resetWeights = () => {
    this.setState({ weights: Object.assign({}, WEIGHT_DEFAULTS) });
  };

  saveWeights = async () => {
    const { weights } = this.state;
    const keys = WEIGHT_FACTORS.map(function(f) { return f.key; });
    const total = keys.reduce(function(a, k) { return a + (parseInt(weights[k]) || 0); }, 0);
    if (total !== 100) {
      this.showToast("Weights must sum to exactly 100 (currently " + total + ").", "error");
      return;
    }
    this.setState({ weightsSaving: true });
    var payload = {};
    keys.forEach(function(k) { payload[k] = parseInt(weights[k]) || 0; });
    const r = await saveScoreWeights(auth.getAccount(), auth.getToken(), payload);
    if (r && r.data && r.data.status === 200) {
      this.setState({ weightsSaving: false });
      this.showToast("Score weights saved. New leads will use the updated formula.", "success");
    } else {
      const msg = (r && r.data && r.data.message) || "Failed to save weights.";
      this.setState({ weightsSaving: false });
      this.showToast(msg, "error");
    }
  };

  patch = (idx, key, value) => {
    this.setState((s) => {
      const stages = s.stages.slice();
      stages[idx] = { ...stages[idx], [key]: value };
      return { stages };
    });
  };

  // Stable, key-based patch for memoized rows. Using stage_key (not idx) keeps
  // the callback identity stable across reorders so React.memo bails out for
  // unchanged rows. Idx-based patch is kept for any legacy callers.
  patchByKey = (stage_key, key, value) => {
    this.setState((s) => {
      const idx = s.stages.findIndex((x) => x.stage_key === stage_key);
      if (idx < 0) return null;
      const stages = s.stages.slice();
      stages[idx] = { ...stages[idx], [key]: value };
      return { stages };
    });
  };

  removeByKey = (stage_key, idx) => {
    return this.doRemoveStage(stage_key, idx);
  };

  save = async (options) => {
    const opts = options || {};
    const silent = !!opts.silent;
    const refreshPipeline = !!opts.refreshPipeline;

    if (this.state.loading || this.state.removing) return;

    // Serialize without blocking further edits: silent saves queue one follow-up,
    // explicit (non-silent) saves still wait their turn so we don't double-toast.
    if (this._isSaving) {
      if (silent) this._pendingSave = true;
      return;
    }

    const stagesWithOrder = this.getStagesForSave(this.state.stages);
    const currentSig = this.getStagesSignature(stagesWithOrder);
    if (currentSig === this._lastSavedStagesSig) return;

    this._isSaving = true;
    this.setState({ saving: true });

    const r = await saveStages(auth.getAccount(), auth.getToken(), stagesWithOrder, { silent });
    this._isSaving = false;

    if (r && r.data && r.data.status === 200) {
      if (silent) {
        // CRITICAL: do not overwrite local state from the server echo on silent
        // saves — the user may be mid-drag or mid-edit. We just trust what we
        // sent and update the baseline signature.
        this._lastSavedStagesSig = currentSig;
        this.setState({ saving: false });
        if (refreshPipeline) {
          this.loadPipelinePreview();
        }
      } else {
        const nextStages = r.data.data || stagesWithOrder;
        this._lastSavedStagesSig = this.getStagesSignature(nextStages);
        this.setState({
          saving: false,
          stages: nextStages,
        });
        this.showToast("Changes saved.", "success");
        // Pipeline preview lead counts can shift when stages change order/active;
        // refresh only on user-driven save to avoid background chatter.
        this.loadPipelinePreview();
      }
    } else {
      this.setState({ saving: false });
      this.showToast("Save failed.", "error");
    }

    // If user changed stages while request was in-flight, queue one more save.
    if (
      this._pendingSave ||
      this.getStagesSignature(this.state.stages) !== this._lastSavedStagesSig
    ) {
      this._pendingSave = false;
      this.scheduleStagesAutoSave();
    }
  };

  addNewStage = () => {
    const self = this;
    const key = "new_" + Date.now();
    const stages = this.state.stages;
    const newStage = {
      stage_key: key,
      label: "New Stage",
      color_hex: "#1C5BDE",
      bg_hex: "#E8EEFB",
      active: 1,
      is_system: 0,
      is_won: 0,
      is_lost: 0,
      sort_order: stages.length + 1,
    };
    const wonLostIndex = stages.findIndex(st => st.is_won === 1 || st.is_lost === 1);
    const updatedStages = stages.slice();
    if (wonLostIndex !== -1) {
      updatedStages.splice(wonLostIndex, 0, newStage);
    } else {
      updatedStages.push(newStage);
    }
    self.setState({ stages: updatedStages, newlyAddedKey: key }, function() {
      setTimeout(function() {
        const el = document.querySelector('[data-stage-key="' + key + '"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 50);
      setTimeout(function() { self.setState({ newlyAddedKey: null }); }, 1800);
    });
  };

  doRemoveStage = async (stage_key, idx) => {
    // If it's a new unsaved stage (starts with 'new_'), just remove from array
    if (String(stage_key).startsWith("new_")) {
      this.setState((s) => ({ stages: s.stages.filter((_, i) => i !== idx) }));
      return;
    }
    this.setState({ removeConfirm: { stage_key: stage_key, idx: idx } });
  };

  cancelRemoveStage = () => {
    if (this.state.removing) return;
    this.setState({ removeConfirm: null });
  };

  confirmRemoveStage = async () => {
    const pending = this.state.removeConfirm;
    if (!pending) return;
    const stage_key = pending.stage_key;
    this.setState({ removing: stage_key });
    const r = await removeStage(auth.getAccount(), auth.getToken(), stage_key);
    if (r && r.data && r.data.status === 200) {
      this.setState({ removing: null, removeConfirm: null, stages: r.data.data });
      this.showToast("Stage removed.", "success");
      await this.loadPipelinePreview();
    } else {
      const msg = (r && r.data && (r.data.message || (r.data.errors && r.data.errors.message))) || "Failed to remove stage.";
      this.setState({ removing: null, removeConfirm: null });
      this.showToast(msg, "error");
    }
  };

  patchNew = (key, value) => {
    this.setState((s) => ({ newStage: { ...s.newStage, [key]: value } }));
  };

  addFromForm = async (saveAfter) => {
    const { newStage } = this.state;
    const label = (newStage.label || "").trim();
    if (!label) return;
    if (this.state.loading || this.state.saving || this.state.removing) return;

    // Persist pending edits first, then create the new stage using dedicated endpoint.
    if (this.getStagesSignature(this.state.stages) !== this._lastSavedStagesSig) {
      await this.save({ silent: true });
    }

    this.setState({ saving: true });
    const r = await addStage(auth.getAccount(), auth.getToken(), {
      label: label,
      color_hex: newStage.color_hex,
      bg_hex: newStage.bg_hex,
    });

    if (r && r.data && r.data.status === 200) {
      const stages = (r.data.data || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      const created = stages.find((s) =>
        String(s.label || "").trim().toLowerCase() === label.toLowerCase() &&
        String(s.color_hex || "").toLowerCase() === String(newStage.color_hex || "").toLowerCase() &&
        String(s.bg_hex || "").toLowerCase() === String(newStage.bg_hex || "").toLowerCase()
      );
      const createdKey = created ? created.stage_key : null;

      this._lastSavedStagesSig = this.getStagesSignature(stages);
      this.setState(
        {
          stages: stages,
          saving: false,
          newlyAddedKey: createdKey,
          newStage: { label: "", color_hex: "#1C5BDE", bg_hex: "#E8EEFB" },
        },
        function() {
          if (createdKey) {
            setTimeout(function() {
              const el = document.querySelector('[data-stage-key="' + createdKey + '"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }, 50);
            setTimeout(() => { this.setState({ newlyAddedKey: null }); }, 1800);
          }
        }
      );
      if (saveAfter) this.showToast("Changes saved.", "success");
      await this.loadPipelinePreview();
    } else {
      const msg = (r && r.data && (r.data.message || (r.data.errors && r.data.errors.message))) || "Failed to add stage.";
      this.setState({ saving: false });
      this.showToast(msg, "error");
    }
  };

  /* ─── Drag and Drop (react-beautiful-dnd) ──────────────────────── */
  onDragEnd = (result) => {
    const { source, destination } = result;
    
    // Dropped outside the list
    if (!destination) return;
    
    // No change in position
    if (source.index === destination.index) return;
    
    const stages = [...this.state.stages];
    
    // Find indices of Won and Lost stages
    const wonLostIndices = stages.reduce((acc, st, idx) => {
      if (st.is_won === 1 || st.is_lost === 1) acc.push(idx);
      return acc;
    }, []);
    const firstWonLostIdx = wonLostIndices.length > 0 ? Math.min(...wonLostIndices) : stages.length;
    
    // Prevent dropping after Won/Lost stages
    if (destination.index >= firstWonLostIdx) return;
    
    const [movedItem] = stages.splice(source.index, 1);
    stages.splice(destination.index, 0, movedItem);
    
    // Optimistic local reorder, then flush save immediately (no 500ms debounce
    // for reorders — that's what made dragging feel "stuck").
    this.setState({ stages }, () => {
      if (this._stagesAutoSaveTimer) {
        clearTimeout(this._stagesAutoSaveTimer);
        this._stagesAutoSaveTimer = null;
      }
      this.save({ silent: true, source: "drag", refreshPipeline: true });
    });
  };

  render() {
    const { stages, loading, saving, removing, pipelineData, loadingPipeline, newlyAddedKey, newStage, removeConfirm,
            weights, weightsLoading, weightsSaving, toast } = this.state;
    const weightTotal = WEIGHT_FACTORS.reduce(function(a, f) { return a + (parseInt(weights[f.key]) || 0); }, 0);
    const weightsValid = weightTotal === 100;
    return (
      <div className="nk-content">
        {ReactDOM.createPortal(
          <div style={{ position: "fixed", top: 16, right: 16, zIndex: 9999, width: 340, maxWidth: "calc(100% - 32px)" }}>
            <NotificationToast
              type={toast ? toast.type : "success"}
              message={toast ? toast.msg : ""}
              duration={2000}
              onDismiss={() => this.setState({ toast: null })}
            />
          </div>,
          document.body
        )}
        <div className="container-fluid">
          <div className="nk-content-inner">
            <div className="nk-content-body">
              <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                  <div className="nk-block-head-content">
                    <h3 className="nk-block-title page-title" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span>Leads & Pipeline Settings</span>
                      {saving ? (
                        <span style={{
                          fontSize: 11, fontWeight: 600, color: "#1c5bde",
                          background: "#e8eefb", padding: "3px 10px", borderRadius: 12,
                          letterSpacing: "0.03em", textTransform: "uppercase",
                        }}>Saving…</span>
                      ) : null}
                    </h3>
                    <div className="nk-block-des text-soft">
                      <p>Define the stages of your sales pipeline. Drag to reorder. Toggle to activate or deactivate.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="example-alert" style={{ marginBottom: 12 }}>
                <div className="alert alert-pro alert-info">
                  <div className="alert-text">
                    <p><em className="icon ni ni-info" style={{ marginRight: 6 }}></em><strong>Drag rows to reorder stages in the pipeline.</strong> Inactive stages are hidden from the Kanban board and funnel. <strong>Won</strong> and <strong>Lost</strong> are system stages and cannot be removed.</p>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                {/* ── Stages Table ── */}
                <div className="col-lg-8 col-md-12">
                  <div className="card card-bordered">
                    <div className="card-inner p-0">
                      <DragDropContext onDragEnd={this.onDragEnd}>
                        <div className="table-responsive">
                        <style>{`
                          table.stages-tbl { display: table !important; width: 100%; }
                          table.stages-tbl thead { display: table-header-group !important; }
                          table.stages-tbl tbody { display: table-row-group !important; }
                          table.stages-tbl tr { display: table-row !important; }
                          table.stages-tbl th,
                          table.stages-tbl td { display: table-cell !important; }
                        `}</style>
                        <table className="table table-tranx stages-tbl" style={{ tableLayout: "fixed", minWidth: 600 }}>
                          <thead className="tb-tnx-head">
                            <tr className="tb-tnx-item">
                              <th className="tb-tnx-info" style={{ width: 44 }}></th>
                              <th className="tb-tnx-info">STAGE NAME</th>
                              <th className="tb-tnx-info" style={{ width: 220 }}>COLOR</th>
                              <th className="tb-tnx-info" style={{ width: 70, textAlign: "center" }}>LEADS</th>
                              <th className="tb-tnx-info" style={{ width: 90, textAlign: "center" }}>ACTIVE</th>
                              <th className="tb-tnx-info" style={{ width: 60, textAlign: "center" }}></th>
                            </tr>
                          </thead>
                          <Droppable droppableId="stages-list">
                            {(provided, snapshot) => (
                              <tbody
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{
                                  background: snapshot.isDraggingOver ? "#f9fafb" : "transparent",
                                }}
                              >
                                {loading ? (
                                  <tr><td colSpan={6} style={{ textAlign: "center", padding: 24 }}>Loading…</td></tr>
                                ) : stages.length === 0 ? (
                                  <tr><td colSpan={6} style={{ textAlign: "center", padding: 24 }}>No stages. Click "Add Stage" to create one.</td></tr>
                                ) : stages.map((s, i) => {
                                  const isLocked = s.is_won === 1 || s.is_lost === 1;
                                  return (
                                    <Draggable key={s.stage_key} draggableId={String(s.stage_key)} index={i} isDragDisabled={isLocked}>
                                      {(provided, snapshot) => (
                                        <StageRow
                                          stage={s}
                                          index={i}
                                          provided={provided}
                                          snapshot={snapshot}
                                          isLocked={isLocked}
                                          isHighlight={s.stage_key === newlyAddedKey}
                                          leadsCount={this._stageCountMap[s.stage_key] || 0}
                                          isRemoving={removing === s.stage_key}
                                          onPatch={this.patchByKey}
                                          onRemove={this.removeByKey}
                                        />
                                      )}
                                    </Draggable>
                                  );
                                })}
                                {provided.placeholder}
                              </tbody>
                            )}
                          </Droppable>
                        </table>
                        </div>
                      </DragDropContext>
                    </div>
                    {/* ── Quick Add Stage ── */}
                    <div style={{ borderTop: "1px solid #f1f2f4", background: "#fafbfc", padding: "16px 20px" }}>
                      <div style={{ marginBottom: 10 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#8094ae" }}>Quick Add Stage</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
                        <div style={{ flex: "1 1 180px", minWidth: 120 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#526484", display: "block", marginBottom: 4 }}>Stage Name</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g. Demo Scheduled"
                            value={newStage.label}
                            onChange={(e) => this.patchNew("label", e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") this.addFromForm(false); }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#526484", display: "block", marginBottom: 4 }}>Text Color</label>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <input
                              type="color"
                              value={newStage.color_hex}
                              onChange={(e) => this.patchNew("color_hex", e.target.value)}
                              style={{ width: 32, height: 32, borderRadius: 6, border: "1.5px solid #d9dfe9", padding: 2, cursor: "pointer" }}
                            />
                            <code style={{ fontSize: 10, color: "#8094ae" }}>{newStage.color_hex}</code>
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#526484", display: "block", marginBottom: 4 }}>BG Color</label>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <input
                              type="color"
                              value={newStage.bg_hex}
                              onChange={(e) => this.patchNew("bg_hex", e.target.value)}
                              style={{ width: 32, height: 32, borderRadius: 6, border: "1.5px solid #d9dfe9", padding: 2, cursor: "pointer" }}
                            />
                            <code style={{ fontSize: 10, color: "#8094ae" }}>{newStage.bg_hex}</code>
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, fontWeight: 600, color: "#526484", display: "block", marginBottom: 4 }}>Preview</label>
                          <span style={{
                            display: "inline-block", padding: "4px 14px", borderRadius: 12,
                            fontSize: 12, fontWeight: 600,
                            color: newStage.color_hex || "#1C5BDE",
                            background: newStage.bg_hex || "#E8EEFB",
                          }}>{newStage.label || "Stage Name"}</span>
                        </div>
                        <div style={{ display: "flex", gap: 6, flex: "1 1 auto", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-light"
                            onClick={() => this.addFromForm(false)}
                            disabled={!newStage.label.trim()}
                          >
                            <em className="icon ni ni-plus" style={{ marginRight: 4 }}></em>Add to List
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{ background: THEME_COLOR, color: THEME_TEXT_COLOR }}
                            onClick={() => this.addFromForm(true)}
                            disabled={!newStage.label.trim() || saving}
                          >
                            <em className="icon ni ni-check-circle" style={{ marginRight: 4 }}></em>Add & Save
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Pipeline Preview ── */}
                <div className="col-lg-4 col-md-12">
                  <div className="card card-bordered">
                    <div className="card-inner">
                      <h6 className="title mb-3" style={{ textTransform: "uppercase", fontSize: 11, letterSpacing: 1, color: "#8094ae" }}>
                        PIPELINE PREVIEW
                      </h6>
                      {loadingPipeline ? (
                        <div style={{ textAlign: "center", padding: 20, color: "#aaa" }}>Loading preview…</div>
                      ) : pipelineData.length === 0 ? (
                        <div style={{ textAlign: "center", padding: 20, color: "#aaa" }}>No stages to preview</div>
                      ) : (
                        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                          {pipelineData.map((item) => {
                            const stage = item.stage;
                            const count = item.count || 0;
                            return (
                              <li
                                key={stage.stage_key}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "10px 0",
                                  borderBottom: "1px solid #f5f6fa",
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                  <span
                                    style={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: "50%",
                                      background: stage.color_hex || "#1C5BDE",
                                      flexShrink: 0,
                                    }}
                                  ></span>
                                  <span style={{ fontSize: 13, fontWeight: 500, color: "#364a63" }}>
                                    {stage.label}
                                  </span>
                                  {stage.is_won ? <span style={{ fontSize: 10, color: "#1a7a4a" }}>✓</span> : null}
                                  {stage.is_lost ? <span style={{ fontSize: 10, color: "#dc3545" }}>✗</span> : null}
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 600, color: "#526484" }}>{count}</span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                  {/* ── Pipeline Tips ── */}
                  <div className="card card-bordered" style={{ marginTop: 16 }}>
                    <div className="card-inner">
                      <h6 style={{ textTransform: "uppercase", fontSize: 11, letterSpacing: "0.07em", color: "#8094ae", marginBottom: 12 }}>Pipeline Tips</h6>
                      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                        {[
                          { icon: "ni-menu",        text: "Drag rows to reorder stages — order determines funnel position." },
                          { icon: "ni-toggle-on",     text: "Inactive stages are hidden from Kanban and funnel but keep their data." },
                          { icon: "ni-lock-alt",      text: "Won and Lost are system stages and cannot be removed or reordered." },
                          { icon: "ni-color-palette", text: "Use high-contrast text and background colors for better readability." },
                          // { icon: "ni-check-circle",  text: "Click \"Save Pipeline\" to commit all edits at once." },
                        ].map((tip, idx) => (
                          <li key={idx} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                            <em className={"icon ni " + tip.icon} style={{ fontSize: 14, color: "#6b7a99", marginTop: 2, flexShrink: 0 }}></em>
                            <span style={{ fontSize: 12, color: "#526484", lineHeight: 1.5 }}>{tip.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {removeConfirm ? (
                <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.45)" }} onClick={this.cancelRemoveStage}>
                  <div className="modal-dialog modal-sm modal-dialog-centered" role="document" onClick={function(e) { e.stopPropagation(); }}>
                    <div className="modal-content">
                      <div className="modal-header" style={{ borderBottom: "2px solid #dc3545" }}>
                        <h5 className="modal-title" style={{ color: "#dc3545" }}>
                          <em className="icon ni ni-alert-circle" style={{ marginRight: 6 }}></em>
                          Remove stage?
                        </h5>
                      </div>
                      <div className="modal-body">
                        <p style={{ marginBottom: 8, fontSize: 13, color: "#526484" }}>
                          This stage will be permanently removed from your pipeline.
                        </p>
                        <p style={{ marginBottom: 0, fontSize: 13, color: "#dc3545", fontWeight: 600 }}>
                          <em className="icon ni ni-lock-alt" style={{ marginRight: 6 }}></em>
                          Stages with active leads assigned cannot be removed.
                        </p>
                      </div>
                      <div className="modal-footer" style={{ gap: 8 }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-light"
                          onClick={this.cancelRemoveStage}
                          disabled={!!removing}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={this.confirmRemoveStage}
                          disabled={!!removing}
                        >
                          {removing ? "Removing..." : "Remove stage"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* ─── Score Weights Editor ─── */}
              <div className="row g-3" style={{ marginTop: 8 }}>
                <div className="col-12">
                  <div className="card card-bordered">
                    <div className="card-inner">
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
                        <div>
                          <h6 style={{ textTransform: "uppercase", fontSize: 11, letterSpacing: "0.07em", color: "#8094ae", marginBottom: 4 }}>
                            ⚡ Lead Score Formula Weights
                          </h6>
                          <p style={{ fontSize: 12, color: "#526484", margin: 0 }}>
                            Each factor contributes a weighted sub-score (0–100). Weights must sum to exactly 100.
                            Changes take effect on the next score recalculation.
                          </p>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                          {weightsValid ? null : (
                            <button type="button" className="btn btn-sm btn-outline-light"
                              onClick={this.normalizeWeights}
                            >Normalize to 100</button>
                          )}
                          <button type="button" className="btn btn-sm btn-outline-light"
                            onClick={this.resetWeights}
                          >Reset Defaults</button>
                          <button type="button" className="btn btn-sm"
                            style={{ background: THEME_COLOR, color: THEME_TEXT_COLOR }}
                            onClick={this.saveWeights}
                            disabled={weightsSaving || !weightsValid}
                          >{weightsSaving ? "Saving…" : "Save Weights"}</button>
                        </div>
                      </div>

                      {weightsLoading ? (
                        <div style={{ textAlign: "center", padding: 24, color: "#aaa" }}>Loading weights…</div>
                      ) : (
                        <div>
                          <div className="row g-3">
                            {WEIGHT_FACTORS.map((f) => {
                              const v = parseInt(weights[f.key]) || 0;
                              return (
                                <div className="col-lg-6" key={f.key}>
                                  <div style={{ padding: "12px 16px", border: "1px solid #e5e9f2", borderRadius: 8, background: "#fafbfc" }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                                      <div>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: "#364a63" }}>{f.label}</span>
                                        <div style={{ fontSize: 11, color: "#8094ae", marginTop: 1 }}>{f.desc}</div>
                                      </div>
                                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <input
                                          type="text"
                                          inputMode="numeric"
                                          pattern="[0-9]*"
                                          maxLength={3}
                                          value={String(v)}
                                          onChange={(e) => this.patchWeight(f.key, e.target.value.replace(/[^0-9]/g, ""))}
                                          onFocus={(e) => e.target.select()}
                                          style={{ width: 58, textAlign: "center", padding: "3px 6px", border: "1px solid #d9dfe9", borderRadius: 5, fontSize: 14, fontWeight: 700, color: "#1c5bde" }}
                                        />
                                        <span style={{ fontSize: 12, color: "#8094ae" }}>%</span>
                                      </div>
                                    </div>
                                    <input
                                      type="range" min="0" max="100"
                                      value={v}
                                      onChange={(e) => this.patchWeight(f.key, e.target.value)}
                                      style={{ width: "100%", accentColor: "#1c5bde", margin: 0 }}
                                    />
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#c4c9d4", marginTop: 2 }}>
                                      <span>0</span><span>50</span><span>100</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <div style={{
                            marginTop: 16, padding: "12px 16px", borderRadius: 6,
                            background: weightsValid ? "#f0fdf4" : "#fff7ed",
                            border: "1px solid " + (weightsValid ? "#86efac" : "#fed7aa"),
                            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                          }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: weightsValid ? "#166534" : "#9a3412", minWidth: 90 }}>
                              Total: {weightTotal}%
                            </span>
                            {weightsValid ? (
                              <span style={{ fontSize: 12, color: "#166534" }}>
                                ✓ Weights are balanced and ready to save.
                              </span>
                            ) : (
                              <span style={{ fontSize: 12, color: "#9a3412" }}>
                                {weightTotal < 100
                                  ? "Add " + (100 - weightTotal) + "% more to reach 100."
                                  : "Remove " + (weightTotal - 100) + "% — total exceeds 100."}
                                {" "}
                                <span
                                  style={{ color: "#1c5bde", cursor: "pointer", textDecoration: "underline", fontWeight: 600 }}
                                  onClick={this.normalizeWeights}
                                >Auto-normalize</span>
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }
}
